import type { ReportDocument } from "../../services/report";
import { StatusIcon } from "../StatusIcon";
import { isUnresolved, warningText } from "./reportFormat";
import { reportSectionOf } from "./reportSections";
import { ReportSnapshotTable } from "./ReportSnapshotTable";

// Sections 3 to 5 of the sheet: the module's own result summary, its checks and detail tables,
// and its warning panel, rendered from the stored result snapshot through the section registry
// exactly as the study page renders a fresh result - nothing recomputed, nothing converted.
// Then the flat list of unresolved items (§20) from the stored warnings snapshot. A calculation
// type without a section prints an explicit "not yet supported" block naming the module, and a
// snapshot the module's current contract cannot read prints the raw snapshot under a banner
// naming the engine version that stored it; a section is never left out silently.

type ReportResultSectionsProps = {
  document: ReportDocument;
};

function UnresolvedItems({ document }: { document: ReportDocument }) {
  const items = document.warnings_snapshot.filter(isUnresolved);
  return (
    <section aria-label="Unresolved items" data-report-unresolved={items.length}>
      <h3>Unresolved items</h3>
      {items.length === 0 ? (
        <p data-no-warnings="true">No unresolved items.</p>
      ) : (
        <ul data-warning-count={items.length}>
          {items.map((item, index) => {
            const { code, message } = warningText(item);
            return (
              <li key={`${code}-${index}`} data-warning-code={code} data-warning-tone="review">
                <strong>
                  <StatusIcon tone="review" size={16} />
                  {code}
                </strong>
                {": "}
                <span>{message}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function ReportResultSections({ document }: ReportResultSectionsProps) {
  const { module } = document;
  const section = reportSectionOf(module.calculation_type);

  if (section === undefined) {
    return (
      <section
        aria-label="Result"
        data-report-section="result"
        data-report-unsupported={module.calculation_type}
      >
        <h3>Section not yet supported</h3>
        <p>
          Module <strong>{module.module_code} — {module.module_name}</strong> stores the calculation
          type <code>{module.calculation_type}</code>, which this report page cannot lay out yet. The
          stored result is printed below exactly as received.
        </p>
        <ReportSnapshotTable snapshot={document.result_snapshot} caption="Stored result" />
        <UnresolvedItems document={document} />
      </section>
    );
  }

  const blocks = section.blocks(document.result_snapshot, {
    engineVersion: document.engine_version,
  });

  if (blocks === null) {
    return (
      <section aria-label="Result" data-report-section="result" data-report-raw-result="true">
        <h3>Result</h3>
        <p data-report-raw-banner>
          Stored by engine <strong>{document.engine_version}</strong>; the current page cannot
          interpret this shape. The stored result is printed below exactly as received.
        </p>
        <ReportSnapshotTable snapshot={document.result_snapshot} caption="Stored result" />
        <UnresolvedItems document={document} />
      </section>
    );
  }

  return (
    <>
      <div data-report-section="result-summary">{blocks.summary}</div>
      <div data-report-section="checks">{blocks.checks}</div>
      <div data-report-section="warnings">
        {blocks.warnings}
        <UnresolvedItems document={document} />
      </div>
    </>
  );
}

export default ReportResultSections;
